import { useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { type ReactNode, useState } from "react";
import {
  ActivityIndicator,
  Linking,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";

import {
  DISCLAIMER,
  formatAud,
  formatPct,
  gstThresholdBullets,
  gstThresholdHeadline,
  rideshareGstBullets,
  rideshareGstHeadline,
  setAsideBullets,
  setAsideHeadline,
  type GstThresholdOutput,
  type RideshareGstOutput,
  type SetAsideOutput,
} from "@aucash/rules/side-income";

import { AppBar, Button, Card, Chip, Coin, Eyebrow } from "../../src/components/brand";
import { Checkbox, Field, MoneyInput, Segmented } from "../../src/components/controls";
import { SHORT_DISCLAIMER } from "../../src/disclaimer";
import { trpc } from "../../src/trpc";
import { colors, fonts, radius, space, tabular } from "../../src/theme";

// side-income — gig / side-hustle tax calculator, surfaced stateless-v1 (PLAN
// arch decision #1). One screen, three faces over the side-income engine; the
// result renders INLINE (no save-by-id, no auth gate, no persistence). Each
// compute is a stateless `calc.sideIncome.compute` query — input -> output, no
// round-trip to a stored row. Copy comes verbatim from the rules layer's copy
// helpers so the wording can never drift from the API/SEO surfaces (validation
// §3: information, not advice; the set-aside % is an ESTIMATE, the flags STATE
// the cited ATO rule, no "you should register"/"save"/product handoff — the
// rules-layer copy already polices this).

const ASSESSMENT_YEAR = "2025-26";

type Mode = "set-aside" | "gst-threshold" | "rideshare-gst";

const MODE_OPTIONS: ReadonlyArray<{ value: Mode; label: string }> = [
  { value: "set-aside", label: "Tax to set aside" },
  { value: "gst-threshold", label: "GST $75k check" },
  { value: "rideshare-gst", label: "Rideshare GST" },
];

const MODE_COPY: Record<Mode, { eyebrow: string; title: string; blurb: string }> = {
  "set-aside": {
    eyebrow: "FY26 · set-aside",
    title: "How much side-income tax should you set aside?",
    blurb:
      "Side income is taxed on top of your salary, at your marginal rate. See the estimated extra tax and a per-invoice set-aside % so a tax bill doesn't surprise you, from current ATO schedules.",
  },
  "gst-threshold": {
    eyebrow: "FY26 · GST $75k",
    title: "Do you have to register for GST?",
    blurb:
      "GST registration is required once your GST turnover reaches A$75,000, current or projected over a rolling 12 months. Check where your turnover sits against the test and the 21-day deadline.",
  },
  "rideshare-gst": {
    eyebrow: "FY26 · ride-sourcing",
    title: "Does rideshare driving need GST from day one?",
    blurb:
      "Ride-sourcing is taxi travel for GST, so GST applies from your first dollar — the A$75,000 threshold does not apply. Food delivery and other gig work keep the A$75,000 test. See where you stand.",
  },
};

type ComputeResult = Awaited<
  ReturnType<typeof trpc.calc.sideIncome.compute.query>
>;

export default function SideIncomeIndex(): JSX.Element {
  const router = useRouter();

  const [mode, setMode] = useState<Mode>("set-aside");

  // set-aside
  const [salary, setSalary] = useState("");
  const [abnIncome, setAbnIncome] = useState("");
  const [hasStudyLoan, setHasStudyLoan] = useState(false);

  // gst-threshold
  const [currentTurnover, setCurrentTurnover] = useState("");
  const [projectedTurnover, setProjectedTurnover] = useState("");
  const [annualBusinessPurchases, setAnnualBusinessPurchases] = useState("");

  // rideshare-gst
  const [doesRideSourcing, setDoesRideSourcing] = useState(true);
  const [doesFoodDelivery, setDoesFoodDelivery] = useState(false);
  const [doesOtherGig, setDoesOtherGig] = useState(false);
  const [expectedTurnover, setExpectedTurnover] = useState("");

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<ComputeResult | null>(null);

  const num = (s: string): number => Number(s || "0");

  const onModeChange = (next: Mode): void => {
    setMode(next);
    setResult(null);
    setSubmitError(null);
    setErrors({});
  };

  const buildInput = (): {
    input: Parameters<typeof trpc.calc.sideIncome.compute.query>[0]["input"];
  } | null => {
    switch (mode) {
      case "set-aside":
        return {
          input: {
            mode: "set-aside",
            salary: num(salary),
            abnIncome: num(abnIncome),
            hasStudyLoan,
            assessmentYear: ASSESSMENT_YEAR,
          },
        };
      case "gst-threshold":
        return {
          input: {
            mode: "gst-threshold",
            currentTurnover: num(currentTurnover),
            projectedTurnover: num(projectedTurnover),
            annualBusinessPurchases: num(annualBusinessPurchases),
            assessmentYear: ASSESSMENT_YEAR,
          },
        };
      case "rideshare-gst":
        return {
          input: {
            mode: "rideshare-gst",
            doesRideSourcing,
            doesFoodDelivery,
            doesOtherGig,
            expectedTurnover: num(expectedTurnover),
            assessmentYear: ASSESSMENT_YEAR,
          },
        };
    }
  };

  const validate = (): Record<string, string> => {
    const next: Record<string, string> = {};
    if (mode === "set-aside") {
      if (abnIncome.trim() === "")
        next.abn = "Enter the side income you expect this year.";
    }
    if (mode === "gst-threshold") {
      if (currentTurnover.trim() === "" && projectedTurnover.trim() === "")
        next.turnover =
          "Enter your current and/or projected 12-month turnover.";
    }
    if (mode === "rideshare-gst") {
      if (!doesRideSourcing && !doesFoodDelivery && !doesOtherGig)
        next.activity = "Tell us which gig activities you do.";
    }
    return next;
  };

  const onSubmit = (): void => {
    const next = validate();
    setErrors(next);
    const payload = buildInput();
    if (Object.keys(next).length > 0 || payload === null || submitting) return;

    setSubmitting(true);
    setSubmitError(null);
    setResult(null);
    trpc.calc.sideIncome.compute
      .query(payload)
      .then((res) => setResult(res))
      .catch((err: unknown) =>
        setSubmitError(
          err instanceof Error ? err.message : "We couldn't run the calculator.",
        ),
      )
      .finally(() => setSubmitting(false));
  };

  const copy = MODE_COPY[mode];

  return (
    <View style={styles.root}>
      <StatusBar style="dark" />
      <AppBar pill="Side income" />
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.stage}>
          <View style={styles.navRow}>
            <Pressable onPress={() => router.push("/")} accessibilityRole="button">
              <Text style={styles.navLink}>← Calculators</Text>
            </Pressable>
          </View>

          <View style={styles.header}>
            <Coin size={56} />
            <Eyebrow>{copy.eyebrow}</Eyebrow>
            <Text style={styles.title}>{copy.title}</Text>
            <Text style={styles.subtitle}>{copy.blurb}</Text>
            <Text style={styles.shortDisclaimer}>{SHORT_DISCLAIMER}</Text>
          </View>

          <Field label="Which question are you answering?">
            <Segmented<Mode>
              options={MODE_OPTIONS}
              value={mode}
              onChange={onModeChange}
              testIDPrefix="mode"
            />
          </Field>

          {mode === "set-aside" ? (
            <>
              <Field
                label="Your salary / wage income for FY26"
                help="Your PAYG salary already taxed at work. The side income stacks on top of this — leave it 0 if you have no salary."
              >
                <MoneyInput
                  value={salary}
                  onChange={setSalary}
                  testID="input-salary"
                  accessibilityLabel="Your salary or wage income for FY26"
                />
              </Field>
              <Field
                label="Expected net side income for FY26"
                help="Your expected gross side income minus the deductible expenses you'll claim — the figure the extra tax is worked out on."
                error={errors.abn}
              >
                <MoneyInput
                  value={abnIncome}
                  onChange={setAbnIncome}
                  testID="input-abn-income"
                  accessibilityLabel="Expected net side income for FY26"
                />
              </Field>
              <Field label="Do you have a HELP / study loan?">
                <Checkbox
                  checked={hasStudyLoan}
                  onChange={setHasStudyLoan}
                  label="Yes — I have an outstanding HECS-HELP or other study and training loan."
                />
              </Field>
            </>
          ) : null}

          {mode === "gst-threshold" ? (
            <>
              <Field
                label="Current turnover (past 12 months)"
                help="Gross business income for the current month plus the previous 11 — before GST, not profit."
                error={errors.turnover}
              >
                <MoneyInput
                  value={currentTurnover}
                  onChange={setCurrentTurnover}
                  testID="input-current-turnover"
                  accessibilityLabel="Current turnover, past 12 months"
                />
              </Field>
              <Field
                label="Projected turnover (next 12 months)"
                help="Gross business income you expect for the current month plus the next 11."
              >
                <MoneyInput
                  value={projectedTurnover}
                  onChange={setProjectedTurnover}
                  testID="input-projected-turnover"
                  accessibilityLabel="Projected turnover, next 12 months"
                />
              </Field>
              <Field
                label="Annual business purchases (optional)"
                help="GST-inclusive business purchases. Used only to estimate the input tax credits in the voluntary-registration trade-off."
              >
                <MoneyInput
                  value={annualBusinessPurchases}
                  onChange={setAnnualBusinessPurchases}
                  accessibilityLabel="Annual business purchases, GST inclusive"
                />
              </Field>
            </>
          ) : null}

          {mode === "rideshare-gst" ? (
            <Field
              label="Which gig activities do you do?"
              help="Ride-sourcing is taxi travel for GST, so it changes the rule for your whole ABN."
              error={errors.activity}
            >
              <View style={{ gap: space.md }}>
                <Checkbox
                  checked={doesRideSourcing}
                  onChange={setDoesRideSourcing}
                  label="Ride-sourcing — Uber / Didi / Ola passenger trips."
                />
                <Checkbox
                  checked={doesFoodDelivery}
                  onChange={setDoesFoodDelivery}
                  label="Food delivery — Uber Eats / DoorDash / Menulog."
                />
                <Checkbox
                  checked={doesOtherGig}
                  onChange={setDoesOtherGig}
                  label="Other gig work — tasking, asset hire, etc."
                />
              </View>
              <View style={{ height: space.md }} />
              <Text style={styles.label}>Expected total turnover for FY26</Text>
              <Text style={styles.help}>
                Used for the A$75,000 test on the non-ride-sourcing path.
              </Text>
              <MoneyInput
                value={expectedTurnover}
                onChange={setExpectedTurnover}
                testID="input-expected-turnover"
                accessibilityLabel="Expected total turnover for FY26"
              />
            </Field>
          ) : null}

          <Button
            label={submitting ? "Calculating…" : "See your estimate →"}
            onPress={onSubmit}
            disabled={submitting}
            full
            testID="side-income-submit"
          />
          {submitError !== null ? (
            <Text style={styles.submitError}>{submitError}</Text>
          ) : null}

          {submitting && result === null ? (
            <View style={styles.loading}>
              <ActivityIndicator color={colors.honey} />
            </View>
          ) : null}

          {result !== null ? <ResultBlock result={result} /> : null}
        </View>
      </ScrollView>
    </View>
  );
}

/** Inline result — stateless, rendered straight under the form (no save-by-id).
 * The full disclaimer (rules-layer DISCLAIMER) sits at the TOP, before any
 * number (validation §3.2 placement rule). */
function ResultBlock({ result }: { result: ComputeResult }): JSX.Element {
  const { headline, bullets } = renderFor(result);

  return (
    <View style={styles.resultStage}>
      {/* §3.2: full disclaimer at the top, before any number. */}
      <View style={styles.disclaimerBlock}>
        <Text style={styles.disclaimerText}>{DISCLAIMER}</Text>
      </View>

      <View style={styles.resHead}>
        <Eyebrow>Your estimate · FY26</Eyebrow>
        <Chip label="Estimate" tone="est" />
      </View>

      <Card style={{ gap: space.sm }}>
        <Text style={styles.lead}>{headline}</Text>
        {bullets.map((b, i) => (
          <Text key={i} style={styles.bodyText}>
            {b}
          </Text>
        ))}
      </Card>

      <Section title="Where these numbers come from">
        <Text style={styles.bodyText}>
          Figures use the side-income ruleset {result.ruleVersion}, last verified{" "}
          {result.sourcesVerifiedAt}. The figures are estimates; the ATO makes the
          final determination.
        </Text>
        {Object.entries(result.sources).map(([key, url]) => (
          <SourceLink key={key} label={url} url={url} />
        ))}
      </Section>
    </View>
  );
}

/** Pick the rules-layer copy helpers for the result's face. All wording comes
 * from the rules package — no inline figures, no advice framing. The
 * rideshare-from-$0 carve-out is surfaced via the rules `rideshareGstHeadline`
 * / `rideshareGstBullets` (which state the cited ATO ride-sourcing rule). */
function renderFor(result: ComputeResult): {
  headline: string;
  bullets: string[];
} {
  const out = result.output;
  switch (result.mode) {
    case "set-aside": {
      const o = out as SetAsideOutput;
      return { headline: setAsideHeadline(o), bullets: setAsideBullets(o) };
    }
    case "gst-threshold": {
      const o = out as GstThresholdOutput;
      return {
        headline: gstThresholdHeadline(o),
        bullets: gstThresholdBullets(o),
      };
    }
    case "rideshare-gst": {
      const o = out as RideshareGstOutput;
      return {
        headline: rideshareGstHeadline(o),
        bullets: rideshareGstBullets(o),
      };
    }
  }
}

function Section({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

function SourceLink({
  label,
  url,
}: {
  label: string;
  url: string | undefined;
}): JSX.Element | null {
  if (url === undefined) return null;
  return (
    <Pressable
      onPress={() => void Linking.openURL(url)}
      accessibilityRole="link"
      style={styles.sourceLink}
    >
      <Text style={styles.sourceLinkText}>{label}</Text>
    </Pressable>
  );
}

// `formatAud`/`formatPct` are imported for parity with the other screens' result
// helpers; the headline/bullet strings already embed the formatted figures, so
// they aren't called directly here. Referenced to keep the imports meaningful.
void formatAud;
void formatPct;

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: colors.cream },
  scroll: { padding: space.xl, paddingBottom: space.xxl, alignItems: "center" },
  stage: { width: "100%", maxWidth: 560, gap: space.xl },
  navRow: { flexDirection: "row", justifyContent: "space-between" },
  navLink: { fontFamily: fonts.bodySemibold, fontSize: 14, color: colors.honeyDeep },
  header: { gap: space.sm },
  title: {
    fontFamily: fonts.display,
    fontSize: 30,
    lineHeight: 34,
    letterSpacing: -1,
    color: colors.ink,
    marginTop: space.xs,
  },
  subtitle: { fontFamily: fonts.body, fontSize: 16, lineHeight: 23, color: colors.inkSoft },
  shortDisclaimer: {
    fontFamily: fonts.bodySemibold,
    fontSize: 12,
    color: colors.muted,
    marginTop: space.xs,
  },
  label: { fontFamily: fonts.bodySemibold, fontSize: 15, color: colors.ink },
  help: {
    fontFamily: fonts.body,
    fontSize: 13,
    color: colors.inkSoft,
    lineHeight: 19,
  },
  submitError: {
    fontFamily: fonts.bodyMedium,
    fontSize: 14,
    color: colors.danger,
    textAlign: "center",
  },
  loading: { paddingVertical: space.xl, alignItems: "center" },

  resultStage: { gap: space.xl, marginTop: space.sm },
  disclaimerBlock: {
    backgroundColor: colors.sand,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.lg,
  },
  disclaimerText: {
    fontFamily: fonts.body,
    fontSize: 12,
    lineHeight: 18,
    color: colors.inkSoft,
  },
  resHead: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  lead: {
    fontFamily: fonts.displayHeavy,
    fontSize: 24,
    lineHeight: 30,
    letterSpacing: -0.5,
    color: colors.ink,
    ...tabular,
  },
  bodyText: { fontFamily: fonts.body, fontSize: 14.5, lineHeight: 22, color: colors.inkSoft },

  section: { gap: space.md },
  sectionTitle: {
    fontFamily: fonts.display,
    fontSize: 20,
    letterSpacing: -0.4,
    color: colors.ink,
  },
  sourceLink: {
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radius.md,
    padding: space.md,
    backgroundColor: colors.card,
  },
  sourceLinkText: { fontFamily: fonts.bodySemibold, fontSize: 13, color: colors.oceanDeep },
});
